import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Repeat02Icon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {StyledRenewedTooltipContent} from '@/features/projects/components/renewed-tooltip/style'
import {TKey, useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'

type Operation = 'renew' | 'variation' | 'transformation' | 'extension'

export const RenewedTooltip = ({renewedProjectId, operation}: {renewedProjectId: string; operation: Operation}) => {
    const {t} = useTranslation()
    const {
        data: renewedProject,
        isLoading,
        isError
    } = useGetProject({
        urlParams: {
            projectId: renewedProjectId
        },
        params: {
            responseType: 'small'
        }
    })

    const operationToLabel = {
        renew: 'projects:renewed',
        variation: 'projects:varied',
        transformation: 'projects:transformed',
        extension: 'projects:extended'
    } as const satisfies Record<Operation, TKey>

    const renderContent = () => {
        return (
            <StyledRenewedTooltipContent align="center" gap={1} paletteKey="success">
                <Repeat02Icon size={12} />
                <Text palette="success" shade="500" size="textSm" weight="400" style={{textDecoration: 'underline'}}>
                    {t(operationToLabel[operation])}
                </Text>
            </StyledRenewedTooltipContent>
        )
    }

    if (!renewedProject || isLoading || isError) return renderContent()

    return (
        <Tooltip
            content={
                <Flexbox align="start" direction="column">
                    <Text palette="neutral" shade="600" size="textXs" weight="400">
                        {t('projects:next_project')}
                    </Text>
                    <ButtonLink
                        to={`/projects/${renewedProject.id}`}
                        variant="tertiary"
                        size="sm"
                        shape="text"
                        style={{whiteSpace: 'nowrap'}}
                    >
                        {t('projects:code_date', {
                            code: renewedProject.code,
                            date: dayjs(renewedProject.startDate).format('DD/MM/YYYY')
                        })}
                    </ButtonLink>
                </Flexbox>
            }
            variant="light"
            contentProps={{side: 'top'}}
        >
            <div style={{cursor: 'pointer'}}>{renderContent()}</div>
        </Tooltip>
    )
}
